import crest from "@/assets/crest.png"
import type { IncidentLog } from "@/models"
import { useAppConfig } from "@/stores/appConfigStore"
import { css } from "vite-css-in-js"
import { onMounted, provide } from "vue"
import {
	defineComponent,
	inferProps,
	proxyProp,
	ValidationHandler,
	ValidationHandlerKey,
	type ReactiveComponent,
} from "vue-utils"
import { IncidentLogHandlerInjectionKey } from "../shared/incident-form/IncidentLogHandler"
import { PdfViewIncidentHandler } from "./PdfViewIncidentHandler"
import AssetsTasked from "./sections/AssetsTasked"
import IncidentLocation from "./sections/IncidentLocation"
import MainDetails from "./sections/MainDetails"
import Narrative from "./sections/Narrative"

interface Props {
	incident: IncidentLog
}

const headerStyles = css`
	display: flex;
	align-items: center;
	gap: 1em;

	background-color: #f6f6f6;
	padding: 0.75em;
	border-bottom: 1px solid rgba(0, 0, 0, 0.35);
	margin-bottom: 1rem;
`

const formattingStyles = css`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;

	& > section {
		& > h4 {
			padding-bottom: 0.5rem;
			border-bottom: 1px solid rgba(0, 0, 0, 0.25);
			font-size: 1.25em;
		}

		& + section {
			border-top: 1px solid rgba(0, 0, 0, 0.75);
			padding-top: 1rem;
		}
	}

	input,
	textarea,
	select,
	.ck-content {
		width: 100%;
		border: 1px solid rgba(0, 0, 0, 0.35) !important;
		border-radius: 0;
	}

	label {
		gap: 0.25rem;
	}
`

const pageStyles = `
	body {
		background-color: white;

		@media print {
			font-size: 10px;
		}
	}
`

const PdfIncidentView: ReactiveComponent<Props> = (props) => {
	const config = useAppConfig()
	const incident = proxyProp(props, "incident")

	const validationHandler = new ValidationHandler({ form: () => undefined })
	provide(ValidationHandlerKey, validationHandler)

	const handler = new PdfViewIncidentHandler(incident)
	provide(IncidentLogHandlerInjectionKey, handler)

	onMounted(() => {
		const currentTitle = document.title
		document.title = `SARLog Incident ${props.incident.identifier}`

		window.print()
		window.close()
		document.title = currentTitle
	})

	/*
		Render the page in the background (but with 0% opacity so its invisible), before showing the content 2s later.
		Gives all CKEditors time to properly load so they're shown in the PDF.
	*/

	return () => (
		<>
			<header class={headerStyles}>
				<img style={{ height: "4em" }} src={crest} alt="Logo Crest" />
				<h1 style={{ fontSize: "2em", margin: 0 }}>{config.appName}</h1>
			</header>
			<div class={formattingStyles}>
				<style innerHTML={pageStyles} />
				<section>
					<h4>Main Details</h4>
					<MainDetails incident={incident} />
				</section>
				<section>
					<h4>Incident Location</h4>
					<IncidentLocation incident={incident} />
				</section>
				<section>
					<h4>Assets Tasked</h4>
					<AssetsTasked incident={incident} />
				</section>
				<section>
					<h4>Narrative</h4>
					<Narrative incident={incident} />
				</section>
			</div>
		</>
	)
}

export default defineComponent(PdfIncidentView, inferProps<Props>())
